import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useCallback } from 'react';
import { User } from '@/types/user';
import { UseFormReturn, Controller } from 'react-hook-form';
import { UserFormValues } from '@/api/types';
import { toast } from '@/hooks/use-toast';
import {
  Divider,
  Grid,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Box,
} from '@mui/material';
import { MySection } from '../custom/my-section';

type UserFormMode = 'create' | 'update';

interface UserFormProps {
  form: UseFormReturn<UserFormValues>;
  mode: UserFormMode;
  onSubmit?: (
    values: UserFormValues,
    context: { mode: UserFormMode },
  ) => Promise<User | { data: User } | void>;
  onSuccess?: (saved: User) => void;
  onCancel?: () => void;
}

export function UserForm({ form, mode, onSubmit, onSuccess, onCancel }: UserFormProps) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const handleFormSubmit = useCallback(
    async (values: UserFormValues) => {
      if (!onSubmit) return;
      try {
        const result = await onSubmit(values, { mode });
        const saved = (result as { data?: User })?.data ?? (result as User | undefined);

        if (saved) {
          toast({
            title: mode === 'create' ? 'User created successfully' : 'User updated successfully',
          });
          onSuccess?.(saved);
        }
        return saved;
      } catch (error: any) {
        toast({
          title: 'Operation failed',
          description: error?.message ?? 'Please check your inputs.',
          variant: 'destructive',
        });
        throw error;
      }
    },
    [mode, onSubmit, onSuccess, toast],
  );

  return (
    <form id="user-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <Grid container spacing={2} paddingY={2} paddingX={2}>
        <Grid item xs={12} md={8} paddingRight={2}>
          <MySection
            title="User details"
            description="Provide the essential information about the user."
          >
            {/* Name Section (Side-by-Side) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className={errors.firstName ? 'text-destructive' : ''}>
                  First Name
                </Label>
                <Input
                  id="firstName"
                  placeholder="John"
                  {...register('firstName')}
                  className={
                    errors.firstName ? 'border-destructive focus-visible:ring-destructive' : ''
                  }
                />
                {errors.firstName && (
                  <p className="text-xs font-medium text-destructive">{errors.firstName.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className={errors.lastName ? 'text-destructive' : ''}>
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  placeholder="Doe"
                  {...register('lastName')}
                  className={
                    errors.lastName ? 'border-destructive focus-visible:ring-destructive' : ''
                  }
                />
                {errors.lastName && (
                  <p className="text-xs font-medium text-destructive">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            {/* Account Info Section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className={errors.username ? 'text-destructive' : ''}>
                  Username
                </Label>
                <Input
                  id="username"
                  placeholder="johndoe123"
                  {...register('username')}
                  className={
                    errors.username ? 'border-destructive focus-visible:ring-destructive' : ''
                  }
                />
                {errors.username && (
                  <p className="text-xs font-medium text-destructive">{errors.username.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className={errors.email ? 'text-destructive' : ''}>
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  {...register('email')}
                  className={
                    errors.email ? 'border-destructive focus-visible:ring-destructive' : ''
                  }
                />
                {errors.email && (
                  <p className="text-xs font-medium text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className={errors.phone ? 'text-destructive' : ''}>
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+66 1234 5678"
                  {...register('phone')}
                  className={
                    errors.phone ? 'border-destructive focus-visible:ring-destructive' : ''
                  }
                />
                {errors.phone && (
                  <p className="text-xs font-medium text-destructive">{errors.phone.message}</p>
                )}
              </div>

              {mode === 'create' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="password" className={errors.password ? 'text-destructive' : ''}>
                      Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      {...register('password')}
                      className={
                        errors.password ? 'border-destructive focus-visible:ring-destructive' : ''
                      }
                    />
                    {errors.password && (
                      <p className="text-xs font-medium text-destructive">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="confirmPassword"
                      className={errors.confirmPassword ? 'text-destructive' : ''}
                    >
                      Confirm Password
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      {...register('confirmPassword')}
                      className={
                        errors.confirmPassword
                          ? 'border-destructive focus-visible:ring-destructive'
                          : ''
                      }
                    />
                    {errors.confirmPassword && (
                      <p className="text-xs font-medium text-destructive">
                        {errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </MySection>
        </Grid>
        <Grid item xs={12} md={4} marginTop={2}>
          <MySection
            title="User Permissions"
            description="Provide the essential information about the user."
          >
            <div className="flex flex-col space-y-4 p-4 border rounded-xl bg-muted/10">
              {/* Roles Field */}
              <div className="space-y-2">
                <Label
                  htmlFor="roles"
                  className="text-xs font-bold uppercase tracking-tight text-muted-foreground ml-1"
                >
                  Roles
                </Label>
                <Controller
                  control={control}
                  name="roles"
                  render={({ field, fieldState: { error } }) => {
                    const roleOptions = [
                      { value: 'ROLE_USER', label: 'User (Standard Access)' },
                      { value: 'ROLE_MANAGER', label: 'Manager (Elevated Access)' },
                      { value: 'ROLE_ADMIN', label: 'Admin (Full System Access)' },
                    ];

                    const selectedRoles = field.value || [];

                    const renderValue = (selected: string[]) => {
                      if (selected.length === 0) {
                        return <Box sx={{ color: 'text.disabled' }}>Select roles...</Box>;
                      }
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const option = roleOptions.find((opt) => opt.value === value);
                            return (
                              <Chip
                                key={value}
                                label={option?.label || value}
                                size="small"
                                sx={{
                                  backgroundColor: '#2563EB',
                                  color: 'white',
                                  '& .MuiChip-deleteIcon': {
                                    color: 'white',
                                  },
                                }}
                              />
                            );
                          })}
                        </Box>
                      );
                    };

                    return (
                      <FormControl fullWidth error={!!error}>
                        <Select
                          {...field}
                          multiple
                          displayEmpty
                          value={selectedRoles}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(typeof value === 'string' ? value.split(',') : value);
                          }}
                          renderValue={renderValue}
                          sx={{
                            '& .MuiSelect-select': {
                              minHeight: '40px',
                            },
                          }}
                        >
                          {roleOptions.map((option) => {
                            const isSelected = selectedRoles.includes(option.value);
                            return (
                              <MenuItem key={option.value} value={option.value}>
                                <Checkbox
                                  checked={isSelected}
                                  size="small"
                                  sx={{
                                    color: '#2563EB',
                                    '&.Mui-checked': {
                                      color: '#2563EB',
                                    },
                                    '&.MuiCheckbox-root:hover': {
                                      backgroundColor: 'rgba(37, 99, 235, 0.1)',
                                    },
                                  }}
                                />
                                {option.label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                        {error && <FormHelperText error>{error.message}</FormHelperText>}
                      </FormControl>
                    );
                  }}
                />
              </div>

              {/* Status Checkbox Block */}
              <div className="space-y-2">
                <Controller
                  control={control}
                  name="state"
                  render={({ field }) => (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value === 1}
                            onChange={(event) => field.onChange(event.target.checked ? 1 : 0)}
                            sx={{
                              color: '#2563EB',
                              '&.Mui-checked': {
                                color: '#2563EB',
                              },
                              '&.MuiCheckbox-root:hover': {
                                backgroundColor: 'rgba(37, 99, 235, 0.1)',
                              },
                            }}
                          />
                        }
                        label={
                          <div className="flex flex-col space-y-0.5">
                            <span className="text-sm font-semibold">Active Account Status</span>
                            <span className="text-xs text-muted-foreground leading-none">
                              Allow this user to log into the platform
                            </span>
                          </div>
                        }
                      />
                      {errors.state && (
                        <FormHelperText error>{errors.state.message}</FormHelperText>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
          </MySection>
          <Divider sx={{ my: 2, border: 0 }} />
        </Grid>
      </Grid>
    </form>
  );
}
